import { Suspense } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { AppShell } from '@/components/layout/app-shell';
import { ContentSkeleton } from '@/components/layout/content-skeleton';
import { ShellSkeleton } from '@/components/layout/shell-skeleton';
import { PanelDeLlamada } from '@/components/softphone/panel-de-llamada';
import { useSession } from '@/lib/entitlements';
import { SoftphoneProvider } from '@/lib/softphone/softphone-provider';
import { CanvasFrame, ErrorScreen } from './error-screen';
import { LOGIN_PATH, TENANT_PICKER_PATH } from './paths';

/**
 * The authenticated frame.
 *
 * The shell is mounted once and stays mounted: navigating between two loaded
 * modules only swaps what is inside the panel, which is what keeps the repaint
 * under the budget and the layout from shifting.
 */
export function RootLayout() {
  const { status, retry } = useSession();
  const location = useLocation();

  if (status === 'loading') return <ShellSkeleton />;

  if (status === 'error') {
    return (
      <CanvasFrame>
        <ErrorScreen
          title="No pudimos cargar tu organización"
          description="La sesión no alcanzó a resolverse. Reintenta; si sigue igual, vuelve a entrar."
          onRetry={retry}
        />
      </CanvasFrame>
    );
  }

  const desde = `${location.pathname}${location.search}`;

  // Sesión viva y ninguna organización elegida. No es el login: la contraseña ya
  // se dio, lo que falta es a cuál de sus organizaciones entrar. El destino
  // viaja con la redirección para volver acá una vez elegida.
  if (status === 'choosing') {
    return <Navigate to={TENANT_PICKER_PATH} replace state={{ from: desde }} />;
  }

  if (status === 'anonymous') {
    return <Navigate to={LOGIN_PATH} replace state={{ from: desde }} />;
  }

  return (
    <SoftphoneProvider>
      <AppShell>
        <Suspense fallback={<ContentSkeleton />}>
          <Outlet />
        </Suspense>
      </AppShell>
      <PanelDeLlamada />
    </SoftphoneProvider>
  );
}
